import React, { useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  TableRow,
  TableCell,
  Typography,
  Avatar,
  Stack,
  Box,
  Chip,
  Tooltip,
} from '@mui/material';
import WarehouseTableHead from './WarehouseTableHead';
import { CommonContext, WarehouseMasterContext } from '../../context';
import moment from 'moment/moment';
import { Cancel, CheckCircle, EditNoteOutlined, Person2 } from '@mui/icons-material';
import TableSkeleton from '../common/TableSkeleton';
import commonApi from '../../utils/api';
import { scrollToTop } from '../common/scrollToTop';

export default function WarehouseTable() {
  const { firstLoading, allData, allWarehouse, selected, setSelected, page, setPage, rowsPerPage, setRowsPerPage, viewDetail, setIsLoading, onLoadApi } = useContext(WarehouseMasterContext);
  const { setOpenAlert, setAlertMessage, setSeverity } = useContext(CommonContext);
  const navigate = useNavigate();

  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('created_at');

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(event.target.value);
    setPage(0);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = allWarehouse.map((item) => item.id);
      setSelected(newSelecteds);
    } else {
      setSelected([]);
    }
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  const sortedRows = useMemo(() => {
    return [...allWarehouse].sort((a, b) => {
      if (order === 'asc') {
        return a[orderBy] > b[orderBy] ? 1 : -1;
      } else {
        return a[orderBy] < b[orderBy] ? 1 : -1;
      }
    });
  }, [allWarehouse, orderBy, order]);

  const toggleStatus = (id, status) => {
    setIsLoading(true);
    commonApi.post('/warehouse-master/status', { id: id, status: status })
      .then((response) => {
        setIsLoading(false);
        setOpenAlert(true);
        scrollToTop();
        if (response?.data?.s === 1) {
          setAlertMessage(response?.data?.msg);
          setSeverity('success');
          onLoadApi();
        } else {
          setAlertMessage(response?.data?.msg);
          setSeverity('error')
        }
      })
      .catch((error) => {
        setIsLoading(false);
        setOpenAlert(true);
        setAlertMessage(error?.message || 'Something went wrong');
        setSeverity('error')
        console.error('Error toggling status:', error);
      });
  }

  const stickyActionStyle = {
    position: 'sticky',
    right: 0,
    backgroundColor: '#fff',
    borderLeft: '1px solid #ddd',
    boxShadow: 'inset 4px 0 8px -4px rgba(0, 0, 0, 0.2)',
    zIndex: 3,
  };

  return (
    <div className='table-component'>
      {firstLoading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2">
            {/* <strong>{selected.length}</strong> Warehouses Selected */}
          </Typography>
          <TablePagination
            rowsPerPageOptions={[20, 50, 100, 150, 200, 250]}
            component="div"
            count={allData?.TotalRows || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      ) : null}
      <TableContainer>
        <Table size="small">
          <WarehouseTableHead
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            selected={selected}
            onSelectAllClick={handleSelectAllClick}
            rowCount={sortedRows.length}
          />
          <TableBody>
            {!firstLoading ? (
              <TableRow>
                <TableCell colSpan={12} align="center" sx={{ p: 0 }}>
                  <TableSkeleton rows={10} columns={12} />
                </TableCell>
              </TableRow>
            ) : sortedRows.length > 0 ?
              sortedRows.map((row, index) => {
                const isItemSelected = isSelected(row.id);
                return (
                  <TableRow
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    key={row.id}
                    selected={isItemSelected}
                  >
                    <TableCell component="th" scope="row">
                      {row.id}
                    </TableCell>
                    <TableCell align="left">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar sx={{ width: 32, height: 32, bgcolor: '#004d40' }}>
                          {row?.name ? row.name.trim().charAt(0).toUpperCase() : <Person2 />}
                        </Avatar>
                        <Typography className='custome-typography'>
                          <Tooltip title={row.name} placement="top" arrow>
                            <div className='no-wrap'><strong>{row.name}</strong></div>
                          </Tooltip>
                          <Tooltip title={row.contact_person} placement="top" arrow>
                            <div className='no-wrap'><small>{row.contact_person}</small></div>
                          </Tooltip>
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="left">{row?.contact_mobile ?? 'NA'}</TableCell>
                    <TableCell align="left">{row?.total_emp ?? '0'}</TableCell>
                    <TableCell align="left">{row?.city}, {row?.state_info?.state || row.state}</TableCell>
                    <TableCell align="left">{row?.pincode ?? 'NA'}</TableCell>
                    <TableCell align="left">{row?.area_per_sqft ?? '0'}</TableCell>
                    <TableCell align="left">
                      <Typography
                        variant="body2"
                        sx={{ cursor: 'pointer', color: 'primary.main', fontWeight: 'bold' }}
                        onClick={() => navigate('/master-warehouse-merchant', { state: { warehouse_master_id: row.id } })}
                      >
                        {row?.merchant_assign_count ?? '0'}
                      </Typography>
                    </TableCell>
                    <TableCell align="left">{row.created_at ? moment(row.created_at).format('DD-MM-YYYY') : 'NA'}</TableCell>
                    <TableCell align="left" sx={{ whiteSpace: 'nowrap' }}>
                      <Chip
                        variant="outlined"
                        label={row?.status === 1 ? 'Active' : 'InActive'}
                        color={row?.status === 1 ? 'success' : 'error'}
                        size="small"
                        icon={row?.status === 1 ? <CheckCircle fontSize="small" /> : <Cancel fontSize="small" />}
                        onClick={() => toggleStatus(row.id, row?.status === 1 ? 0 : 1)}
                      />
                    </TableCell>
                    <TableCell align="left" sx={stickyActionStyle}>
                      <Stack direction="row" spacing={1}>
                        <Chip
                          variant="outlined"
                          label={'Edit'}
                          color={'default'}
                          size="small"
                          icon={<EditNoteOutlined fontSize="small" />}
                          onClick={() => viewDetail(row.id)}
                        />
                        <Chip
                          variant="outlined"
                          label={'Assigned Merchant'}
                          color={'primary'}
                          size="small"
                          icon={<Person2 fontSize="small" />}
                          onClick={() => {
                            navigate('/master-warehouse-merchant', { state: { warehouse_master_id: row.id } });
                          }}
                        />
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              }) : (
                <TableRow>
                  <TableCell colSpan={12} align="center">
                    No data found
                  </TableCell>
                </TableRow>
              )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}
